/** Transaction envelope and result (ADR-009 §3.2). */
import type { Intent } from './intents';
import type { ActorRef, DocRef, LayerId } from './scene-view';

export interface TransactionRequest {
  /** Client idempotency key, [A-Za-z0-9._-]{1,64}. */
  txId: string;
  document: DocRef;
  baseRevision: number;
  /** Default 'scoped' (§6.3). */
  concurrency?: 'scoped' | 'strict';
  /** ≤ 80 chars, shown in History; sanitised like ContentString. */
  label: string;
  /** 1..200 (LIMITS.maxIntents). */
  intents: Intent[];
  /** Validate and diff; never commits. */
  dryRun?: boolean;
  /** Default false for agents: the user's layer selection is restored after commit. */
  adoptSelection?: boolean;
}

export const REJECTION_CODES = [
  'SCHEMA_INVALID', 'UNSUPPORTED_COMMAND', 'SESSION_GONE', 'NO_LIVE_SESSION',
  'STALE_REVISION', 'CONFLICT', 'USER_EDITING', 'LAYER_BUSY',
  'LAYER_NOT_FOUND', 'LAYER_LOCKED', 'UNSUPPORTED_FOR_KIND', 'COMMAND_REJECTED',
  'PERMISSION_DENIED', 'CONSENT_DENIED', 'CONSENT_TIMEOUT', 'LIMIT_EXCEEDED', 'TX_ID_REUSED',
  'NOT_TOP_OF_HISTORY', 'TX_EVICTED', 'REVERT_CONFLICT', 'UNSUPPORTED_FORMAT',
] as const;
export type RejectionCode = typeof REJECTION_CODES[number];

/** `path` is in SSV terms, e.g. "transform.matrix", "text.style.color", "objects[vector_…].fill". */
export interface FieldChange { layer: LayerId; path: string; before: unknown; after: unknown }

export interface TransactionConflict { revision: number; actor: ActorRef; label: string; layers: LayerId[] }

export interface TransactionDiagnostic { severity: 'info' | 'warning'; code: string; message: string; layer?: LayerId }

export interface TransactionRejection {
  code: RejectionCode;
  intentIndex?: number;
  layer?: LayerId;
  message: string;
  conflicts?: TransactionConflict[];
}

export interface TransactionResult {
  status: 'committed' | 'dry-run' | 'rejected';
  txId: string;
  document: DocRef;
  baseRevision: number;
  revision: number;
  affected: { created: LayerId[]; modified: LayerId[]; deleted: LayerId[]; aliases: Record<string, LayerId> };
  /** Capped at LIMITS.maxChanges entries; `changesTruncated` when more. */
  changes: FieldChange[];
  changesTruncated?: boolean;
  diagnostics: TransactionDiagnostic[];
  rejection?: TransactionRejection;
  history?: { position: number; label: string; undoableBy: 'user' | 'user+this-agent' };
  /** Layers copied by `layer.duplicate` / `layer.paste`, with the id of every copied layer (G12). */
  copies?: LayerCopyReport[];
}

/** One copied layer: `ids` maps every layer of the source subtree to its copy. */
export interface LayerCopyReport { intentIndex: number; source: LayerId; layer: LayerId; ids: Record<LayerId, LayerId> }

/** Dispatcher limits (ADR-009 §3.3 step 3). Schema caps are looser so over-limit requests return LIMIT_EXCEEDED. */
export const LIMITS = Object.freeze({
  maxIntents: 200,
  maxCreates: 64,
  maxTextLength: 10_000,
  maxLabelLength: 80,
  maxChanges: 200,
  maxTransformComponent: 1_000_000,
  liveGestureWaitMs: 2_000,
  /** Points in one `paint.stroke` (editor command); more is LIMIT_EXCEEDED. */
  maxStrokePoints: 20_000,
  /** Points in one `selection.polygon` or `selection.quick`; more is LIMIT_EXCEEDED. */
  maxSelectionPoints: 4_096,
  /** Rectangles in an explicit `selection` param of a pixel command; more is LIMIT_EXCEEDED. */
  maxSelectionRegions: 100_000,
});
